import { useWorkspaceText } from './interface-copy';
import { useEffect, useState } from 'react';
import { Dialog } from 'radix-ui';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionsActionsCopy from '@/i18n/ns/connections-actions';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { sharingApi, audienceLabel, type ShareKind } from './sharing-api';
import { chatCollaborationApi, sharingWarning } from './chat-collaboration-api';
import { ADMIN_PRIVATE_KINDS, INSTANT_COMPANY_KINDS, companyShareNotice } from './sharing-policy';
import { emptyTurnContext, type ShareAccess } from './types';
import { ResultShareWarning } from './living-result';
import { useIntlLocale } from './intl-locale';
import {
  AudienceControl,
  PeopleList,
  PersonRow,
  SheetFooter,
  SheetNote,
  SheetSection,
  ShareSheet,
  ShareTrigger,
  audienceStatus,
  roleOptions,
  sameAudience,
  toSheetAudience,
  toVisibility,
  useColleagues,
  useSheetCopy,
  type SheetAudience,
  type SheetMember,
} from './share-sheet/share-sheet';
import { RunsOn, ViewerShareButton, type RunsOnThing } from './share-sheet/runs-on';
import { ProjectReadsFirstCheck } from './projects/share';

type ShareItem = ShareAccess & {
  id: string;
  title?: string;
  name?: string;
  /** A Skill is named by its draft. */
  draft?: { name?: string } | null;
};

// Shared work that runs on attached connections (resource connection grants).
const RUNS_ON: Partial<Record<ShareKind, { kind: 'result' | 'skill'; thing: RunsOnThing }>> = {
  result: { kind: 'result', thing: 'theResult' },
  skill: { kind: 'skill', thing: 'theSkill' },
};
const itemTitle = (item: ShareItem, fallback: string) =>
  item.title || item.name || item.draft?.name || fallback;

export function ShareButton({
  kind,
  item,
  noun,
  label,
  onSaved,
  audienceReasons,
  openOnRequest,
}: {
  kind: ShareKind;
  item: ShareItem;
  /** The trigger's own words instead of Share (an empty project's "Share with the people doing it"). */
  label?: string;
  /** What the dialog calls the item when it is not its kind (a meeting shares its file). */
  noun?: string;
  /** A page that holds its own copy of the item (a project) reloads it here. */
  onSaved?: () => void;
  /** Choices this person can't make here, with why (a project's "Only you" for a non-lead). */
  audienceReasons?: Partial<Record<SheetAudience, string>>;
  /** Asked once when the button mounts: true opens the sheet (an outside agent's share link, everywhere/agent-link.ts). */
  openOnRequest?: () => boolean;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    // Consumed here, not while rendering: a render React discards must not use up the link.
    if (openOnRequest?.() && item.canShare === true) setOpen(true);
    // Only the first mount reads the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ws = useWorkspace();
  const company = useCompany();
  const copy = useSheetCopy();
  const admin = company.data?.membership?.permissions?.makeCompanyWorkPrivate === true;
  const locale = useIntlLocale();
  const audience = audienceLabel(item, ws.companyName, locale);
  const runsOn = RUNS_ON[kind];
  if (!item.canShare)
    return (
      <>
        {runsOn && item.visibility && item.visibility !== 'private' ? (
          <ViewerShareButton
            kind={runsOn.kind}
            id={item.id}
            thing={runsOn.thing}
            title={itemTitle(item, copy.untitled)}
            selfId={ws.user?.id}
            audienceLabel={audience}
            roleLabel={item.canEdit ? copy.canEdit : copy.canUse}
          />
        ) : (
          <small className="ws-muted">{audience}</small>
        )}
        {admin &&
          ADMIN_PRIVATE_KINDS.has(kind) &&
          item.visibility &&
          item.visibility !== 'private' && <MakePrivateButton kind={kind} item={item} />}
      </>
    );
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <ShareTrigger
          visibility={item.visibility}
          audienceLabel={audience}
          label={label}
          testId="share-button"
        />
      </Dialog.Trigger>
      {open && (
        <ShareDialog
          kind={kind}
          item={item}
          noun={noun}
          onSaved={onSaved}
          audienceReasons={audienceReasons}
          onClose={() => setOpen(false)}
        />
      )}
    </Dialog.Root>
  );
}

// D57 decision 2: the creator or any company admin can make a shared item
// private again. The creator is told in their Inbox.
function MakePrivateButton({
  kind,
  item,
}: {
  kind: ShareKind;
  item: ShareAccess & { id: string };
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const outcome = await sharingApi.save({
        kind,
        id: item.id,
        visibility: 'private',
        audience: [],
        revision: item.sharingRevision,
      });
      setOpen(false);
      // The admin is not its creator, so the item just left their reach: close
      // its canvas rather than reload it into an error.
      if (ws.canvas?.kind === kind && ws.canvas.id === item.id) ws.openCanvas(null);
      await ws.refresh();
      ws.notify(
        outcome.creatorNotified
          ? uiText('Made private. Its creator has been told in their Inbox.')
          : uiText('Made private. Its creator could not be notified.'),
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : uiText('Could not make this private.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger className="ws-button">{uiText('Make private')}</Dialog.Trigger>
      {open && (
        <Dialog.Portal>
          <Dialog.Overlay className="ws-finder-scrim" />
          <Dialog.Content className="ws-share-dialog">
            <div className="ws-share-heading">
              <Dialog.Title>{uiRich('Make this {value1} private?', { value1: kind })}</Dialog.Title>
              <Dialog.Close className="ws-icon-button" aria-label={uiText('Close')}>
                <WorkspaceIcon name="close" />
              </Dialog.Close>
            </div>
            <Dialog.Description>
              {uiText(
                'Only the person who created it will see it. Everyone else loses access right away, and its creator gets an Inbox update if they are still a company member. If they have left, nobody in the company will be able to access this item.',
              )}
            </Dialog.Description>
            {error && (
              <p className="ws-error" role="alert">
                {error}
              </p>
            )}
            <div className="ws-actions">
              <Dialog.Close className="ws-button">{uiText('Cancel')}</Dialog.Close>
              <button className="ws-button primary" disabled={busy} onClick={() => void confirm()}>
                {busy ? uiText('Saving…') : uiText('Make private')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      )}
    </Dialog.Root>
  );
}

// A chat's Share dialog warns before it widens: colleagues will read what the
// assistant or an App found in accounts the whole company cannot use — the
// owner's own, or one shared with them through Specific people — and can never
// use them (D57 decision 3). The server lists the private connections it used.
function useChatSharingWarning(kind: ShareKind, id: string) {
  const [warning, setWarning] = useState<ReturnType<typeof sharingWarning> | null>(null);
  useEffect(() => {
    if (kind !== 'chat') return;
    let active = true;
    void chatCollaborationApi
      .sharingCheck(id)
      .then((value) => {
        if (active && value.privateConnections.length)
          setWarning(sharingWarning(value.privateConnections));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [kind, id]);
  return warning;
}

export function ShareDialog({
  kind,
  item,
  noun,
  onClose,
  initialVisibility,
  onSaved,
  audienceReasons = {},
}: {
  kind: ShareKind;
  item: ShareItem;
  noun?: string;
  onClose: () => void;
  onSaved?: () => void;
  audienceReasons?: Partial<Record<SheetAudience, string>>;
  /** Opened from a chat's scope chip with the audience already picked. */
  initialVisibility?: 'private' | 'members' | 'company';
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const company = useCompany();
  const copy = useSheetCopy();
  const actionCopy = useNamespaceCopy(connectionsActionsCopy);
  const locale = useIntlLocale();
  const chat = kind === 'chat';
  const before = item.visibility ?? 'private';
  const [audience, setAudience] = useState<SheetAudience>(
    toSheetAudience(initialVisibility ?? before),
  );
  const [members, setMembers] = useState<SheetMember[]>(item.audience ?? []);
  const colleagues = useColleagues();
  const warning = useChatSharingWarning(kind, item.id);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canPublish = company.data?.membership?.permissions?.publishCompany === true;
  // Whether a company share applies at once or waits for a publisher.
  const instant = canPublish || INSTANT_COMPANY_KINDS.has(kind);
  const visibility = toVisibility(audience);
  const people = visibility === 'members' ? members : [];
  const dirty =
    visibility !== before ||
    (visibility === 'members' && !sameAudience(members, item.audience ?? []));
  const runsOn = RUNS_ON[kind];
  const title = itemTitle(item, copy.untitled);
  async function save() {
    setBusy(true);
    setError('');
    try {
      const saved = await sharingApi.save({
        kind,
        id: item.id,
        visibility,
        audience: people,
        revision: item.sharingRevision,
      });
      if (chat) ws.refreshThreadMeta();
      else if (onSaved) onSaved();
      else await ws.refresh();
      ws.notify(
        saved.status === 'pending'
          ? uiText('Sent for review. The current audience stays unchanged until approval.')
          : copy.updated,
      );
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : uiText('Could not update sharing.'));
    } finally {
      setBusy(false);
    }
  }
  const changeInChat = () => {
    onClose();
    ws.send(
      formatCopy(copy.changesPrompt, { kind: noun ?? kind, id: item.id, name: title }),
      emptyTurnContext(),
      true,
      null,
      { newChat: true },
    );
  };
  const blocked =
    busy ||
    (kind === 'skill' && visibility === 'company' && before !== 'company') ||
    (visibility === 'members' && (!members.length || colleagues.loading));
  return (
    <ShareSheet
      eyebrow={copy.eyebrowShare}
      title={title}
      busy={busy}
      testId="share-sheet"
      status={
        error ? (
          <span role="alert">{error}</span>
        ) : dirty ? (
          copy.statusPending
        ) : (
          audienceStatus(copy, locale, audience, people.length, ws.companyName)
        )
      }
      actions={
        <SheetFooter
          dirty={dirty}
          busy={busy}
          disabled={blocked}
          saveLabel={visibility === 'company' && !instant ? actionCopy.requestApproval : undefined}
          onSave={() => void save()}
        />
      }
    >
      <SheetSection
        title={copy.whoTitle}
        help={
          chat && audience !== 'private'
            ? uiText(
                'They can follow new answers live and fork it to continue with their own connections. Only you write here. You can make it private again at any time.',
              )
            : kind === 'project' && audience !== 'private'
              ? uiText(
                  'They see the project, its brief and the work in it they can already open. Nothing in it opens for anyone because of the project; each item keeps its own access.',
                )
              : !chat && audience !== 'private'
                ? uiText(
                    'Check the content for private information before sharing. Sharing an item does not grant access to its original chat or private connections.',
                  )
                : undefined
        }
      >
        <AudienceControl
          value={audience}
          companyName={ws.companyName}
          disabled={busy}
          reasons={
            kind === 'skill' && before !== 'company'
              ? { ...audienceReasons, company: copy.skillCompany }
              : audienceReasons
          }
          onChange={setAudience}
        />
        {chat && audience !== 'private' && warning?.own && (
          <SheetNote>
            {uiRich(
              '{value1} Everyone you share with can read it, including what the assistant or an App in it found in your {value2}. They can’t use your {value3}. Nothing changes for you: the chat keeps working with all your accounts.',
              {
                value1: (
                  <strong>
                    {uiRich('This chat used your {value1}.', { value1: warning.own })}
                  </strong>
                ),
                value2: warning.own,
                value3: warning.own,
              },
            )}
          </SheetNote>
        )}
        {chat &&
          audience !== 'private' &&
          warning?.others.map((account) => (
            <SheetNote key={account}>
              {uiRich(
                '{value1} Everyone you share with can read them, but nobody can use {value2} through this chat.',
                {
                  value1: (
                    <strong>
                      {uiRich('This chat has results from {value1}.', { value1: account })}
                    </strong>
                  ),
                  value2: account,
                },
              )}
            </SheetNote>
          ))}
        {kind === 'result' && audience !== 'private' && <ResultShareWarning id={item.id} />}
        {kind === 'project' && audience !== 'private' && (
          <ProjectReadsFirstCheck
            projectId={item.id}
            company={audience === 'company'}
            userIds={members
              .filter(
                (person) =>
                  !(item.audience ?? []).some((before) => before.userId === person.userId),
              )
              .map((person) => person.userId)}
          />
        )}
        {audience === 'company' && (
          <PersonRow
            id="company"
            company
            name={formatCopy(copy.everyone, { company: ws.companyName })}
            detail={companyShareNotice(kind, canPublish, ws.companyName, locale)}
          />
        )}
        {audience === 'people' && (
          <PeopleList
            members={members}
            colleagues={colleagues.people}
            loading={colleagues.loading}
            failed={colleagues.failed}
            roles={roleOptions(copy, chat ? 'chat' : kind === 'file' ? 'file' : 'work')}
            exclude={ws.user?.id ? [ws.user.id] : []}
            disabled={busy}
            onChange={setMembers}
          />
        )}
        {kind === 'skill' && (
          <p className="ws-sharesheet-help">
            {uiText(
              'This controls access to the working draft. Any published company version stays available until you explicitly unpublish it. To publish a new version, complete its preview and use Save Skill. Run results stay private.',
            )}
          </p>
        )}
      </SheetSection>
      {runsOn && (
        <RunsOn
          kind={runsOn.kind}
          id={item.id}
          thing={runsOn.thing}
          selfId={ws.user?.id}
          chatLabel={copy.editInChat}
          onChangeInChat={changeInChat}
        />
      )}
    </ShareSheet>
  );
}
